import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import Platform from 'platform';
import apiCubic from 'config/api-cubic';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import { getContractNumberSelector, getIsLoggedInSelector } from 'reducers/profile/selectors';

export const KEY = 'USE_LOAD_PACKAGES';

const NON_AUTH_USER_DATA = {
  Browser: '80639557944',
  Tizen: '902350077',
  WebOS: '902350078',
  Hisense: '902350079',
  Vewd: '80639557944',
};

const getPackageId = (pkgId, idx) => {
  // replace not allowed characters for DOM node id attribute
  const validId = pkgId.replace(/^[^a-z]+|[^\w:.-]+/gi, '');
  return `package-${validId}-${idx}`;
};

const fixSctructure = ({ units, tmCode, subTmCode, ...pkg }, idx) => {
  const id = tmCode || subTmCode;
  const domId = getPackageId(id, idx);
  const isAdditional = !!subTmCode;
  const updated = { ...pkg, id, domId, isAdditional };
  // TODO: comment
  if (units) updated.unit = units;

  return updated;
};

export const getMainSubs = async ({ data, isLoggedIn }) => {
  const [active, subs] = await Promise.all([
    isLoggedIn ? apiCubic.post({ endpoint: apiCubic.endpoints.getActiveSubscription, data }) : null,
    apiCubic.post({ endpoint: apiCubic.endpoints.getSubscriptions, data }),
  ]);

  return [...(active?.data?.payload || []), ...subs.data.payload].map(fixSctructure);
};

export const getAdditionalSubs = async ({ data, isLoggedIn }) => {
  const [active, subs] = await Promise.all([
    isLoggedIn ? apiCubic.post({ endpoint: apiCubic.endpoints.getActiveAdditionSubscriptions, data }) : null,
    apiCubic.post({ endpoint: apiCubic.endpoints.getAdditionalSubscriptions, data }),
  ]);

  return [...(active?.data?.payload || []), ...subs.data.payload].map(fixSctructure);
};

export const getFiltered = async ({ data, filters }) => {
  const filtered = await apiCubic.post({
    endpoint: apiCubic.endpoints.getSubscriptionsByAssetId,
    data: {
      ...data,
      assetType: filters.vodAssetId ? 'vodAssetId' : 'channelId',
      ID: filters.vodAssetId || filters.channelId,
    },
  });

  return {
    main: (filtered.data?.tmPackage ?? []).map(fixSctructure),
    additional: (filtered.data?.subTmPackage ?? []).map(fixSctructure),
  };
};

export const get = async ({ contractCode, isLoggedIn, filters } = {}) => {
  const data = {
    contractCode: contractCode,
    language: INIT_UI_LANGUAGE,
  };

  if (filters) {
    return getFiltered({ data, filters });
  } else {
    const main = await getMainSubs({ data, isLoggedIn });
    const additional = await getAdditionalSubs({ data, isLoggedIn });
    return { main, additional };
  }
};

const useLoadPackages = (filters = null, options = {}) => {
  const platformName = Platform.getName();
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const contractCode = useSelector(getContractNumberSelector) || NON_AUTH_USER_DATA[platformName];

  return useQuery([KEY, contractCode, isLoggedIn, filters], () => get({ contractCode, isLoggedIn, filters }), {
    ...options,
  });
};

export default useLoadPackages;
